import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Read the user's IANA timezone from the browser. Sent on every auth call
// so the backend can persist it the moment the user touches the system,
// without waiting for them to open the Dashboard. Returns null on failure.
function _browserTimezone() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz && typeof tz === 'string' ? tz : null;
  } catch (_) { return null; }
}

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CRITICAL: If returning from Google OAuth callback, skip the /auth/me
    // check. AuthCallback will exchange the session_id and establish the
    // session first. See /app/auth_testing.md.
    if (typeof window !== 'undefined' && window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(data);
      // Cache the profile so offline navigation (and the local-first
      // wellness queues that depend on `user` being present) still works
      // when the browser can't reach /auth/me. Previously we only wrote
      // this cache on the refresh path, which meant a fresh login + going
      // offline left us with no cached profile.
      try { localStorage.setItem('healthloop_user', JSON.stringify(data)); } catch (_) {}
    } catch (error) {
      // 401 here usually means the short-lived access_token expired while
      // the long-lived refresh_token is still valid (e.g. user closed the
      // app yesterday and is reopening now). Try a one-shot silent refresh
      // before declaring the session dead — that's the whole point of the
      // refresh cookie.
      if (error.response?.status === 401) {
        try {
          const headers = {};
          if (typeof window !== 'undefined') {
            const lsRefresh = window.__hlAuth?.getRefresh?.();
            if (lsRefresh) headers.Authorization = `Bearer ${lsRefresh}`;
          }
          const refreshRes = await axios.post(
            `${API}/auth/refresh`,
            {},
            { withCredentials: true, headers },
          );
          if (refreshRes?.data?.tokens && typeof window !== 'undefined') {
            window.__hlAuth?.storeTokens?.(refreshRes.data.tokens);
          }
          const { data } = await axios.get(`${API}/auth/me`, { withCredentials: true });
          setUser(data);
          localStorage.setItem('healthloop_user', JSON.stringify(data));
          setLoading(false);
          return;
        } catch (refreshErr) {
          // Refresh genuinely failed — clear any stale tokens and fall through.
          if (typeof window !== 'undefined') window.__hlAuth?.clearTokens?.();
        }
      }
      // Network error (offline) → fall back to cached profile for read-only UI.
      if (!error.response && error.message === 'Network Error') {
        const cachedUser = localStorage.getItem('healthloop_user');
        if (cachedUser) {
          try {
            setUser(JSON.parse(cachedUser));
          } catch (e) {
            setUser(false);
          }
        } else {
          setUser(false);
        }
      } else {
        setUser(false);
      }
    } finally {
      setLoading(false);
    }
  };

  const register = async (email, password, name) => {
    try {
      const tz = _browserTimezone();
      const { data } = await axios.post(
        `${API}/auth/register`,
        { email, password, name },
        {
          withCredentials: true,
          headers: tz ? { 'X-Browser-Timezone': tz } : {},
        }
      );
      // Persist any returned cross-origin tokens before flipping user state.
      if (data?.tokens && typeof window !== 'undefined') {
        window.__hlAuth?.storeTokens?.(data.tokens);
      }
      setUser(data);
      try { localStorage.setItem('healthloop_user', JSON.stringify(data)); } catch (_) {}
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const login = async (email, password) => {
    try {
      const tz = _browserTimezone();
      const { data } = await axios.post(
        `${API}/auth/login`,
        { email, password },
        {
          withCredentials: true,
          headers: tz ? { 'X-Browser-Timezone': tz } : {},
        }
      );
      // Persist tokens BEFORE setUser so any immediate downstream axios call
      // already has the Bearer header attached by the request interceptor.
      if (data?.tokens && typeof window !== 'undefined') {
        window.__hlAuth?.storeTokens?.(data.tokens);
      }
      setUser(data);

      // Cache user for offline access
      localStorage.setItem('healthloop_user', JSON.stringify(data));
      // Successful sign-in clears any stale session-expired banner.
      try { sessionStorage.removeItem('healthloop_session_expired'); } catch (_) { /* storage disabled */ }

      return { success: true, user: data };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // ALWAYS clear local state — even if the network call fails, we want
      // the user to be locally logged out immediately.
      setUser(false);
      localStorage.removeItem('healthloop_user');
      if (typeof window !== 'undefined') {
        window.__hlAuth?.clearTokens?.();
      }
    }
  };

  const updateProfile = async (profileData) => {
    try {
      const { data } = await axios.put(`${API}/auth/profile`, profileData, {
        withCredentials: true,
      });
      setUser(data);
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const completeOnboarding = async (onboardingData) => {
    try {
      const { data } = await axios.put(`${API}/auth/onboarding`, onboardingData, {
        withCredentials: true,
      });
      setUser(data);
      return { success: true };
    } catch (error) {
      const message = formatApiErrorDetail(error.response?.data?.detail) || error.message;
      return { success: false, error: message };
    }
  };

  const value = {
    user,
    loading,
    register,
    login,
    logout,
    updateProfile,
    completeOnboarding,
    refreshUser: checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
